import { Component, OnInit, Input, Output, EventEmitter, inject, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbDropdownModule, NgbModal, NgbTooltipModule, NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { SimplebarAngularModule } from 'simplebar-angular';
import { Subscription, forkJoin } from 'rxjs';
import { TimeAgoPipe } from '@shared/pipes/time-ago.pipe';
import { NotificationPollingService } from '@modules/backoffice/notification-context/notification/service/notification-polling.service';
import { Notification } from '@modules/backoffice/notification-context/notification/model/notification.model';
import { NotificationViewModalComponent } from '@modules/backoffice/notification-context/notification/consult/notification-view-modal.component';
import { Router } from '@angular/router';

@Component({
  standalone: true,
  selector: 'app-dropdown-notification',
  templateUrl: './dropdown-notification.component.html',
  styleUrls: ['./dropdown-notification.component.scss'],
  imports: [CommonModule, NgbDropdownModule, NgbTooltipModule, FormsModule, SimplebarAngularModule, TimeAgoPipe],
})
export class DropdownNotificationComponent implements OnInit, OnDestroy {
  @Input() currLang: string = 'fr';

  notifications: Notification[] = [];
  notificationList: { title: string; items: Notification[] }[] = [];

  totalNotify: number = 0;
  newNotify: number = 0;
  readNotify: number = 0;

  checkedValGet: (number | undefined)[] = [];

  private readonly notificationPollingService = inject(NotificationPollingService);
  private  notificationSubscription: Subscription | undefined;
  private readonly modalService = inject(NgbModal);

  @Output() notificationsUpdated = new EventEmitter<void>();

  // Use @ViewChild to get a reference to the NgbDropdown directive instance
  @ViewChild('notificationDropdown', { static: false })
  ngbDropdown!: NgbDropdown;

  constructor(private readonly router: Router) {}

  ngOnInit(): void {
    this.notificationSubscription = this.notificationPollingService.getNotifications().subscribe((notifs) => {
      this.notifications = notifs.map((notif) => ({
        ...notif,
        read: notif.lu ?? false,
        status: notif.etat,
        state: notif.state ?? false,
        avatar: notif.avatar ?? '',
        background: notif.background ?? 'bg-info-subtle text-info',
        icon: notif.icon ?? 'bi bi-info-circle',
        name: notif.titre ?? 'System Notification',
        timestamp: notif.dateCreation ? new Date(notif.dateCreation) : new Date(),
        text: this.extractTextFromHtml((this.currLang === 'ar' ? notif.contenuAr : notif.contenu) ?? notif.titre ?? '', 100),
      }));
      this.groupNotifications();
      this.calculateNotificationCounts();
    });
  }

  /**
   * Extrait le texte d'une chaîne HTML et limite la longueur
   */
  private extractTextFromHtml(html: string, maxLength: number = 100): string {
    if (!html) return '';

    // Créer un élément temporaire pour parser le HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Extraire le texte
    const text = tempDiv.textContent || tempDiv.innerText || '';

    // Limiter la longueur et ajouter des points de suspension si nécessaire
    if (text.length > maxLength) {
      return text.substring(0, maxLength).trim() + '...';
    }

    return text.trim();
  }

  ngOnDestroy(): void {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }
  }

  private groupNotifications(): void {
    const newNotifications: Notification[] = [];
    const earlierNotifications: Notification[] = [];

    this.notifications.forEach((notif) => {
      if (!notif.read) {
        newNotifications.push(notif);
      } else {
        earlierNotifications.push(notif);
      }
    });

    this.notificationList = [];
    if (newNotifications.length > 0) {
      this.notificationList.push({ title: 'New', items: newNotifications });
    }
    if (earlierNotifications.length > 0) {
      this.notificationList.push({
        title: 'Earlier',
        items: earlierNotifications,
      });
    }
  }

  openNotificationModal(notification: Notification): void {
    const modalRef = this.modalService.open(NotificationViewModalComponent, {
      size: 'lg',
      centered: true,
    });
    modalRef.componentInstance.notification = notification;
    modalRef.componentInstance.currLang = this.currLang;

    if (notification.id && !notification.read) {
      this.notificationPollingService.update({ id: notification.id, lu: true }).subscribe({
        next: () => {
          notification.read = true;
          notification.lu = true;
          this.groupNotifications();
          this.calculateNotificationCounts();
          this.notificationsUpdated.emit();
        },
        error: (err) => console.error('Erreur lors de la mise à jour du statut de lecture via la modale:', err),
      });
    }

    modalRef.result
      .then((result) => {
      })
      .catch((reason) => {
      });
  }

  onCheckboxChange(event: Event, notificationId: number | undefined) {
    const checkbox = event.target as HTMLInputElement;

    if (checkbox.checked) {
      if (notificationId !== undefined && !this.checkedValGet.includes(notificationId)) {
        this.checkedValGet.push(notificationId);
      }
    } else {
      this.checkedValGet = this.checkedValGet.filter((id) => id !== notificationId);
    }
    this.toggleNotificationActionsVisibility();
  }

  onNotificationDelete() {
    const idsToDelete = this.checkedValGet.filter((id) => id !== undefined) as number[];

    if (idsToDelete.length > 0) {
      const deleteObservables = idsToDelete.map((id) => this.notificationPollingService.delete(id));

      forkJoin(deleteObservables).subscribe({
        next: () => {
          this.notifications = this.notifications.filter((notif) => notif.id !== undefined && !idsToDelete.includes(notif.id));
          this.groupNotifications();
          this.calculateNotificationCounts();
          this.checkedValGet = [];
          this.toggleNotificationActionsVisibility();
          this.notificationsUpdated.emit();
        },
        error: (error) => {
          console.error('Erreur lors de la suppression des notifications:', error);
        },
      });
    } else {
      this.groupNotifications();
      this.calculateNotificationCounts();
      this.checkedValGet = [];
      this.toggleNotificationActionsVisibility();
    }
  }

  onMarkAllAsRead(): void {
    const ops = this.notifications
      .filter((notif) => !notif.read)
      .map((el) => {
        const el2 = { id: el.id, lu: true };
        return this.notificationPollingService.update(el2);
      });

    if (ops.length === 0) {
      return;
    }

    forkJoin(ops).subscribe({
      next: () => {
        this.notifications.forEach((notif) => {
          notif.read = true;
          notif.lu = true;
        });
        this.groupNotifications();
        this.calculateNotificationCounts();
        this.checkedValGet = [];
        this.toggleNotificationActionsVisibility();
        this.notificationsUpdated.emit();
      },
      error: (err) => console.error('Erreur lors du marquage de toutes les notifications comme lues:', err),
    });
  }

  onDeleteAllNotifications(): void {
    const allNotificationIds = this.notifications.map((notif) => notif.id).filter((id) => id !== undefined) as number[];

    if (allNotificationIds.length > 0) {
      const deleteObservables = allNotificationIds.map((id) => this.notificationPollingService.delete(id));

      forkJoin(deleteObservables).subscribe({
        next: () => {
          this.notifications = [];
          this.groupNotifications();
          this.calculateNotificationCounts();
          this.checkedValGet = [];
          this.toggleNotificationActionsVisibility();
          this.notificationsUpdated.emit();
        },
        error: (error) => {
          console.error('Erreur lors de la suppression de toutes les notifications:', error);
        },
      });
    } else {
      this.notifications = [];
      this.groupNotifications();
      this.calculateNotificationCounts();
      this.checkedValGet = [];
      this.toggleNotificationActionsVisibility();
    }
  }

  private toggleNotificationActionsVisibility() {
    const notificationActionsElem = document.getElementById('notification-actions') as HTMLElement;
    if (notificationActionsElem) {
      if (this.checkedValGet.length > 0) {
        notificationActionsElem.style.display = 'flex';
      } else {
        notificationActionsElem.style.display = 'none';
      }
    }
  }

  private calculateNotificationCounts() {
    this.newNotify = this.notifications.filter((notif) => !notif.read).length;
    this.readNotify = this.notifications.filter((notif) => notif.read).length;
    this.totalNotify = this.notificationPollingService.totalCount??0;

    this.toggleNotificationActionsVisibility();
  }

  closeDropdown(): void {
    if (this.ngbDropdown) {
      this.ngbDropdown.close();
    }
    this.checkedValGet = [];
    // Set 'state' to false for all notifications to uncheck them visually
    this.notifications.forEach((notif) => {
      notif.state = false;
    });
    this.toggleNotificationActionsVisibility();
  }

  toList() {
    this.router.navigate(['backoffice/notification-context/notifications-list']);
  }
}
